// Detection and the two pictures (ground truth vs. each side's COP). See spec §4 "Detection" and §7
// "Information layer", and plan Task 9.
//
// stepDetection runs once per sim minute (t % 10 === 0, TICKS_PER_MIN ticks/minute): each alive
// sensor-bearing unit rolls against every living enemy within sensor range and LOS, refreshing or
// creating a contact in its side's Picture on success; stale contacts (unseen for 10 minutes) are
// dropped; UAS airframes roll a survival check under EW; units' inContact flag is recomputed; and each
// side's Picture.reported map is updated with friendly positions (visible to the UI only after report
// latency - see the comment on the `reported` loop below).
//
// Report latency and the feed: a contact's `reportedAt` tick is when it reaches headquarters, and the
// map ring only appears once the sim clock reaches it (Sim.snapshot filters on `reportedAt <= t`). The
// `contact.new`/`nai.contact` events that raise the SPOTREP in the feed must not appear before that:
// each side's DetectionState holds a small pending-report queue, and a newly detected contact's event is
// only pushed (with `t` set to the arrival tick, not the detection tick) once the sim clock reaches it -
// immediately, in the same call, if latency is 0 (dial "off").
import type { Contact, Dials, Graphic, Picture, Side, Unit } from './types';
import { TICKS_PER_MIN } from './types';
import type { Grid } from './grid';
import type { Rng } from './rng';
import type { EventLog } from './events';
import { delayTicks } from './orders';
import { loadRules, type Rules } from './rules';
// Per-type unit definitions live in one place (data/units.json, surfaced by scenario.ts); detection,
// movement, and combat all read the same table.
import { UNIT_DEFS } from './scenario';

export { UNIT_DEFS };

const DEFAULT_RULES = loadRules('realistic');

/** Ticks a contact can go unrefreshed before it is considered stale (10 sim minutes). */
const STALE_TICKS = 10 * TICKS_PER_MIN;

/** A fresh, empty picture for one side. */
export function newPicture(side: Side): Picture {
  return { side, contacts: new Map(), reported: new Map() };
}

/** A feed event whose push to the log is deferred until the report reaches headquarters. */
export interface PendingReport { t: number; push: () => void }

/**
 * Per-Sim state for stepDetection: each side's queue of contact reports that have been detected but not
 * yet delivered. Held on the owning Sim (via `newDetectionState`), mirroring combat.ts's CombatState, so
 * two Sims in one process never share or collide on it.
 */
export interface DetectionState {
  pending: [PendingReport[], PendingReport[]];
  /** Enemy ids each side has ever had reported; the first sighting of a unit is always significant. */
  seen: [Set<number>, Set<number>];
}

/** A fresh, empty DetectionState for a new Sim (or a replay run independent of any live Sim). */
export function newDetectionState(): DetectionState {
  return { pending: [[], []], seen: [new Set(), new Set()] };
}

/** Pushes every pending report whose arrival tick has come, for both sides, in insertion order. Runs
 * every tick (not just detection minute boundaries) so an event lands on the exact tick it arrives. */
function flushPending(t: number, state: DetectionState): void {
  for (const side of [0, 1] as const) {
    const q = state.pending[side];
    if (q.length === 0) continue;
    const remaining: PendingReport[] = [];
    for (const item of q) {
      if (item.t <= t) item.push(); else remaining.push(item);
    }
    q.length = 0;
    if (remaining.length > 0) q.push(...remaining);
  }
}

function hqAliveForSide(side: Side, units: Unit[]): boolean {
  return units.some((u) => u.side === side && u.type === 'hq' && u.alive);
}

/**
 * Ticks for a contact/report to reach command, per spec §4: half of the C2 transmission delay for the
 * reporting unit (delayTicks already folds in c2 dial, EW, in-contact, and HQ-alive factors).
 */
function reportLatencyTicks(dials: Dials, unit: Unit, units: Unit[], rules: Rules): number {
  return Math.round(delayTicks(dials, unit, hqAliveForSide(unit.side, units), rules) / rules.reportLatencyDivisor);
}

/**
 * Probability an `observer` detects `target` this minute. 0 if `target` is out of the observer's sensor
 * range or (for non-UAS observers) has no line of sight. Otherwise base = sensor quality x (1 -
 * dist/range), x movingMult if the target is moving (x staticMult otherwise), x concealment penalty from
 * the target's cell cover, x time-of-day factor. Clamped to [0, 0.95].
 */
export function detectProb(
  observer: Unit, target: Unit, grid: Grid, dials: Dials, distM: number, rules: Rules = DEFAULT_RULES,
): number {
  const def = UNIT_DEFS[observer.type];
  if (distM > def.sensorRangeM) return 0;

  const isUas = observer.type === 'uas';
  if (!isUas) {
    const targetDef = UNIT_DEFS[target.type];
    const los = grid.hasLos(
      { x: observer.x, y: observer.y }, def.heightM,
      { x: target.x, y: target.y }, targetDef.heightM,
    );
    if (!los) return 0;
  }

  const det = rules.detection;
  let p = def.sensorQuality * (1 - distM / def.sensorRangeM);
  p *= target.posture === 'move' ? det.movingMult : det.staticMult;
  const targetCell = grid.cellOfCm(target.x, target.y);
  p *= 1 - grid.coverAt(targetCell) / det.concealmentDivisor;
  p *= dials.tod === 'night' ? det.todNight : dials.tod === 'dawn' ? det.todDawn : det.todDay;

  return Math.min(0.95, Math.max(0, p));
}

/** The 'nai' graphic (if any) whose circle contains `cell`, or null. Circles: center = pts[0], radius = radiusM. */
function containingNai(cell: number, grid: Grid, graphics: Graphic[]): Graphic | null {
  const center = grid.centerCm(cell);
  for (const g of graphics) {
    if (g.kind !== 'nai' || g.pts.length === 0 || g.radiusM === undefined) continue;
    if (grid.distCm(center, g.pts[0]) / 100 <= g.radiusM) return g;
  }
  return null;
}

function otherSide(side: Side): Side { return side === 0 ? 1 : 0; }

/**
 * Advances detection and the two pictures by one sim minute. No-op unless t % 10 === 0 (once per
 * sim minute; TICKS_PER_MIN = 10). `graphics` is blue's graphics list (axes, OBJ, PLs, NAIs); red has no
 * equivalent list here, so NAI significance only ever applies to blue's contacts on red (side 0).
 */
export function stepDetection(
  t: number,
  units: Unit[],
  grid: Grid,
  pictures: [Picture, Picture],
  dials: Dials,
  rng: Rng,
  log: EventLog,
  graphics: Graphic[],
  state: DetectionState,
  rules: Rules = DEFAULT_RULES,
): void {
  // Deliver any reports whose arrival tick has come, every tick, regardless of the detection cadence
  // below (a report's arrival tick is rarely itself a minute boundary).
  flushPending(t, state);

  if (t % TICKS_PER_MIN !== 0) return;

  const ordered = [...units].sort((a, b) => a.id - b.id);

  // 1. Detection rolls: every alive observer against every alive enemy in range/LOS.
  for (const observer of ordered) {
    if (!observer.alive) continue;
    if (observer.type === 'uas' && !observer.uasAlive) continue;

    for (const target of ordered) {
      if (!target.alive || target.side === observer.side) continue;

      const distM = grid.distCm({ x: observer.x, y: observer.y }, { x: target.x, y: target.y }) / 100;
      const prob = detectProb(observer, target, grid, dials, distM, rules);
      if (prob <= 0) continue;
      if (!(rng.float() < prob)) continue;

      const pic = pictures[observer.side];
      const existing = pic.contacts.get(target.id);
      const hadRecentContact = existing !== undefined && t - existing.lastSeen <= STALE_TICKS;

      const quality = UNIT_DEFS[observer.type].sensorQuality;
      const errorM = 50 + distM * 0.1 * (1 - quality);
      // The reported position is the true position perturbed within the error radius (deterministic, seeded),
      // so the picture is genuinely wrong by about errorM, not merely "true position plus a ring".
      const errCm = errorM * 100;
      const px = Math.round((rng.float() * 2 - 1) * errCm * 0.7);
      const py = Math.round((rng.float() * 2 - 1) * errCm * 0.7);
      const contact: Contact = {
        enemyId: target.id, x: target.x + px, y: target.y + py,
        errorM,
        typeConf: distM < 1000 ? target.type : distM < 2500 ? 'vehicle' : 'unknown',
        lastSeen: t,
        reportedAt: t + reportLatencyTicks(dials, observer, units, rules),
        observerId: observer.id,
      };
      pic.contacts.set(target.id, contact);

      if (!hadRecentContact) {
        // The event records what the observer REPORTS (errored position, degraded type), never ground truth:
        // the report feed and every LLM prompt are built from these events. It must not appear in the
        // feed before the report actually reaches headquarters, so it is pushed (with t = the arrival
        // tick) only once the sim clock reaches contact.reportedAt - immediately, here, if the report
        // has zero latency (dial "off"); otherwise queued on the side's DetectionState.
        const targetCell = grid.cellOfCm(contact.x, contact.y);
        const nai = observer.side === 0 ? containingNai(targetCell, grid, graphics) : null;
        const firstSighting = !state.seen[observer.side].has(target.id);
        state.seen[observer.side].add(target.id);
        const reportedAt = contact.reportedAt;
        const pushReport = (): void => {
          log.push(
            reportedAt, 'contact.new', observer.side,
            { enemyId: target.id, typeConf: contact.typeConf, errorM: contact.errorM, observerId: observer.id },
            { unitId: target.id, cell: targetCell, significant: nai !== null || firstSighting },
          );
          if (nai) {
            log.push(
              reportedAt, 'nai.contact', observer.side,
              { graphicId: nai.id, name: nai.name },
              { unitId: target.id, cell: targetCell, significant: true },
            );
          }
        };
        if (reportedAt <= t) pushReport();
        else state.pending[observer.side].push({ t: reportedAt, push: pushReport });
      }
    }
  }

  // 2. Drop stale contacts (unrefreshed for more than 10 minutes).
  for (const side of [0, 1] as const) {
    const pic = pictures[side];
    const staleIds = [...pic.contacts.keys()].filter((id) => t - pic.contacts.get(id)!.lastSeen > STALE_TICKS);
    staleIds.sort((a, b) => a - b);
    for (const id of staleIds) {
      const contact = pic.contacts.get(id)!;
      const cell = grid.cellOfCm(contact.x, contact.y);
      pic.contacts.delete(id);
      log.push(t, 'contact.lost', side, { enemyId: id }, { unitId: id, cell, significant: false });
    }
  }

  // 3. inContact: true if the unit has an enemy contact within its own weapon range, or is the target of
  // a contact freshly (this minute) taken by an enemy unit that is itself within its own weapon range.
  for (const unit of ordered) {
    if (!unit.alive) continue;
    const ownWeaponRangeM = UNIT_DEFS[unit.type].weaponRangeM;
    let inContact = false;

    for (const c of pictures[unit.side].contacts.values()) {
      if (grid.distCm({ x: unit.x, y: unit.y }, { x: c.x, y: c.y }) / 100 <= ownWeaponRangeM) { inContact = true; break; }
    }

    if (!inContact) {
      for (const c of pictures[otherSide(unit.side)].contacts.values()) {
        if (c.enemyId !== unit.id || c.lastSeen !== t) continue;
        const enemyUnit = units.find((u) => u.id === c.observerId);
        if (!enemyUnit || !enemyUnit.alive) continue;
        const enemyRangeM = UNIT_DEFS[enemyUnit.type].weaponRangeM;
        if (grid.distCm({ x: unit.x, y: unit.y }, { x: enemyUnit.x, y: enemyUnit.y }) / 100 <= enemyRangeM) {
          inContact = true; break;
        }
      }
    }

    if (inContact && !unit.inContact) unit.contactSince = t;
    unit.inContact = inContact;
  }

  // 4. UAS survival roll: each minute, every airframe still up rolls against EW-level loss probability.
  const loss = rules.uasLossByEw[dials.ew];
  for (const u of ordered) {
    if (u.type !== 'uas' || !u.alive || !u.uasAlive) continue;
    if (rng.float() < loss) {
      u.uasAlive = false;
      log.push(t, 'uas.lost', u.side, { unitId: u.id }, { unitId: u.id, significant: true });
    }
  }

  // 5. Friendly reported positions. `tick` is stamped t + report latency, not t: the position itself is
  // written now (ground truth as of this minute), but the COP layer must not surface it to the UI until
  // the current sim tick reaches `reported.get(id).tick` - that's what "visible only after report
  // latency" means here. Filtering on that field is the COP/UI layer's job, not this module's.
  for (const u of ordered) {
    if (!u.alive) continue;
    const tick = t + reportLatencyTicks(dials, u, units, rules);
    pictures[u.side].reported.set(u.id, { x: u.x, y: u.y, tick });
  }
}
